import { useEffect, useState } from "react";

import { Alert } from "../components/Alert";
import { Button } from "../components/Button";
import { Field, Input, Select, Textarea } from "../components/Field";
import { Modal, ModalForm } from "../components/Modal";
import { fetchMembers, fetchSites } from "../lib/api";
import { messageOf } from "../lib/errors";
import { useAction } from "../lib/useAction";
import { firstError, required, validateOwningSystem } from "../lib/validate";
import type { CreateServiceMemberInput, Member, Session, Site } from "../lib/types";

/**
 * 서비스 계정 추가 모달.
 *
 * 서버는 service member 에 **활성 human 담당자**를 요구합니다 — 담당자가 비활성화되면 그 계정의
 * 책임자를 추적할 수 있어야 하기 때문입니다. 그래서 담당자 후보를 활성 human 으로 따로 조회하고,
 * 로그인한 본인이 human 이면 기본값으로 둡니다.
 *
 * 소속 시스템 후보는 서비스 카탈로그의 `siteCode` 를 제안만 합니다(datalist) — short-link 발급처럼
 * 카탈로그에 없는 시스템도 service member 를 쓰기 때문에 자유 입력을 막지 않습니다.
 *
 * 제출은 응답까지 `pending` 이고(중복 생성 방지 — 이름 유일성 제약이 없어 서버가 막지 않습니다),
 * 실패하면 창을 유지하고 오류를 창 안에 보여줍니다. 성공 처리(닫기·알림)는 호출부의 `onSubmit` 몫입니다.
 */
export function CreateServiceModal({
  session,
  onClose,
  onSubmit,
}: {
  session: Session;
  onClose: () => void;
  /** reject 되면 메시지를 창 안에 보여주고 창을 유지합니다. */
  onSubmit: (input: CreateServiceMemberInput) => Promise<unknown>;
}) {
  const action = useAction();
  const [owners, setOwners] = useState<Member[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [displayName, setDisplayName] = useState("");
  const [ownerId, setOwnerId] = useState(
    session.member.type === "human" ? String(session.member.memberId) : "",
  );
  const [owningSystem, setOwningSystem] = useState("");
  const [purpose, setPurpose] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchMembers({ type: "human", status: "active", q: "" })
      .then((rows) => {
        if (alive) setOwners(rows);
      })
      .catch((err: unknown) => {
        if (alive) setLoadError(`담당자 목록 조회 실패: ${messageOf(err)}`);
      });
    // 카탈로그는 제안용일 뿐이라 실패(권한 없음 등)해도 폼을 막지 않습니다.
    fetchSites()
      .then((rows) => {
        if (alive) setSites(rows);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  const name = displayName.trim();
  const system = owningSystem.trim();
  const invalid = firstError(
    required(name, "이름을 입력하세요."),
    required(ownerId, "담당자를 고르세요."),
    validateOwningSystem(system),
  );

  const submit = () => {
    setTouched(true);
    if (invalid) return;
    void action.run(() =>
      onSubmit({
        type: "service",
        displayName: name,
        ownerMemberId: Number(ownerId),
        purpose: purpose.trim() || null,
        owningSystem: system || null,
      }),
    );
  };

  /** 응답 대기 중에는 닫지 않습니다 — 요청이 나간 뒤 창만 사라지면 생성 여부를 알 수 없습니다. */
  const close = () => {
    if (!action.pending) onClose();
  };

  return (
    <Modal
      title="서비스 계정 추가"
      sub="다른 시스템이 plt API 를 호출할 때 쓰는 기계용 계정입니다."
      onClose={close}
      footer={
        <>
          <Button variant="ghost" disabled={action.pending} onClick={close}>
            취소
          </Button>
          <Button pending={action.pending} onClick={submit}>
            추가
          </Button>
        </>
      }
      status={
        action.error ? (
          <Alert kind="error">{`추가 실패: ${action.error}`}</Alert>
        ) : touched && invalid ? (
          <Alert kind="warn">{invalid}</Alert>
        ) : null
      }
    >
      {loadError ? <Alert kind="error">{loadError}</Alert> : null}

      <ModalForm>
        <Field id="svc-name" label="이름">
          <Input
            id="svc-name"
            placeholder="예: 연구노트 서비스 계정"
            value={displayName}
            onChange={(ev) => setDisplayName(ev.target.value)}
          />
        </Field>
        <Field id="svc-owner" label="담당자">
          <Select
            id="svc-owner"
            value={ownerId}
            onChange={(ev) => setOwnerId(ev.target.value)}
            options={[
              { value: "", label: "담당자 선택" },
              ...owners.map((m) => ({
                value: String(m.memberId),
                label: m.email ? `${m.displayName} (${m.email})` : m.displayName,
              })),
            ]}
          />
        </Field>
        <Field id="svc-system" label="소속 시스템 (선택)">
          <Input
            id="svc-system"
            list="svc-system-options"
            placeholder="예: rn"
            autoCapitalize="off"
            spellCheck={false}
            value={owningSystem}
            onChange={(ev) => setOwningSystem(ev.target.value)}
          />
          <datalist id="svc-system-options">
            {sites.map((s) => (
              <option key={s.siteId} value={s.siteCode}>
                {s.displayName}
              </option>
            ))}
          </datalist>
          <p className="m-0 text-[12px] text-grey-500">
            통합 로그인을 위임받으려면 서비스 카탈로그의 코드와 같아야 합니다.
          </p>
        </Field>
        <Field id="svc-purpose" label="용도 (선택)">
          <Textarea
            id="svc-purpose"
            placeholder="예: 연구노트 통합 로그인 티켓 교환"
            value={purpose}
            onChange={(ev) => setPurpose(ev.target.value)}
          />
        </Field>
      </ModalForm>
    </Modal>
  );
}
